'use client'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface NativeSelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface NativeSelectGroup {
  label: string
  options: NativeSelectOption[]
}

interface NativeSelectProps {
  value: string
  onChange: (value: string) => void
  options?: NativeSelectOption[]
  groups?: NativeSelectGroup[]
  className?: string
  disabled?: boolean
}

/**
 * A native HTML select component that works reliably inside modals.
 * Use this when Radix dropdowns conflict with modal focus traps (e.g., Clerk UserProfile).
 */
function NativeSelect({
  value,
  onChange,
  options,
  groups,
  className,
  disabled = false,
}: NativeSelectProps) {
  return (
    <div className="relative inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          'bg-muted text-foreground h-8 w-32 cursor-pointer appearance-none rounded-lg py-1.5 pr-8 pl-3 text-sm font-medium outline-none',
          'hover:bg-muted/80',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      >
        {groups
          ? groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))
          : options?.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
      </select>
      <ChevronDown className="text-muted-foreground pointer-events-none absolute top-1/2 right-2.5 h-4 w-4 -translate-y-1/2" />
    </div>
  )
}

export { NativeSelect }
export type { NativeSelectGroup, NativeSelectOption, NativeSelectProps }
